import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { translations, detectLang } from "../lib/i18n";
import { fetchPublicConfig } from "../lib/api";

const AppContext = createContext(null);

const ADMIN_STORAGE_KEY = "vagalume.admin.pwd";

function getInitialTheme() {
  if (typeof window === "undefined") return "dark";
  // Default to dark theme regardless of system preference
  return "dark";
}

function getStoredAdminPassword() {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(ADMIN_STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

export function AppProvider({ children }) {
  const [theme, setTheme] = useState(getInitialTheme);
  const [lang, setLang] = useState(detectLang);
  const [adminPassword, setAdminPasswordState] = useState(getStoredAdminPassword);
  const [restaurantWhatsapp, setRestaurantWhatsapp] = useState("");
  const [stripe, setStripe] = useState({ enabled: false, currency: "EUR" });

  const setAdminPassword = useCallback((pwd) => {
    setAdminPasswordState(pwd);
    try {
      if (pwd) window.sessionStorage.setItem(ADMIN_STORAGE_KEY, pwd);
      else window.sessionStorage.removeItem(ADMIN_STORAGE_KEY);
    } catch {
      /* no-op */
    }
  }, []);

  useEffect(() => {
    fetchPublicConfig()
      .then((cfg) => {
        setRestaurantWhatsapp(cfg.restaurantWhatsapp || "");
        if (cfg.stripe) setStripe(cfg.stripe);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    root.setAttribute("data-theme", theme);
  }, [theme]);

  useEffect(() => {
    document.documentElement.setAttribute("lang", lang);
  }, [lang]);

  const toggleTheme = useCallback(() => {
    setTheme((t) => (t === "dark" ? "light" : "dark"));
  }, []);

  const toggleLang = useCallback(() => {
    setLang((l) => (l === "it" ? "en" : "it"));
  }, []);

  const t = useCallback(
    (key) => translations[lang]?.[key] ?? translations.it[key] ?? key,
    [lang]
  );

  const value = useMemo(
    () => ({
      theme,
      toggleTheme,
      lang,
      toggleLang,
      setLang,
      t,
      adminPassword,
      setAdminPassword,
      restaurantWhatsapp,
      stripe,
    }),
    [theme, toggleTheme, lang, toggleLang, t, adminPassword, setAdminPassword, restaurantWhatsapp, stripe]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
}
